import React, { useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';

import { css } from '@emotion/css';
import { TenantProductName } from '@recurrency/core-api-schema/dist/common/enums';
import { useDebounce } from 'use-debounce/lib';

import { FlexSpace } from 'components/FlexSpace';
import { MiniTableRowSkeleton } from 'components/MiniTable';

import { useEventListener } from 'hooks/useEventListener';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { getIsPlatformMac } from 'utils/responsive';
import { shouldShowProduct } from 'utils/roleAndTenant';
import { TrackEvent, track } from 'utils/track';

import * as Styled from './GlobalSearch.style';
import { MenuCategory, MenuOption } from './types';
import { useGlobalSearch } from './useGlobalSearch';

const Category = ({ category }: { category: MenuCategory }) => <span>{category.label}</span>;

const Option = ({ option, isDesktopView }: { option: MenuOption; isDesktopView: boolean }) => (
  <Styled.ResultContainer to={option.href} isDesktopView={isDesktopView} data-track="Nav.SearchSelectOption">
    <Styled.ResultTitle>
      <Styled.ResultIcon>{option.icon}</Styled.ResultIcon>
      <Styled.ResultTitleContainer>
        <Styled.ResultTitleLine1>
          {option.labelHighlights
            ? option.labelHighlights.map((label, i) =>
                i % 2 ? (
                  <Styled.HighlightedLabel key={i}>{label}</Styled.HighlightedLabel>
                ) : (
                  <Styled.UnhighlightedLabel key={i}>{label}</Styled.UnhighlightedLabel>
                ),
              )
            : option.label}
        </Styled.ResultTitleLine1>
        {option.subLabel ? <Styled.ResultTitleLine2>{option.subLabel}</Styled.ResultTitleLine2> : null}
      </Styled.ResultTitleContainer>
    </Styled.ResultTitle>
    <Styled.ResultSubtitle isDesktopView={isDesktopView}>{option.subtext || ''}</Styled.ResultSubtitle>
  </Styled.ResultContainer>
);

export const GlobalSearch = ({ isDesktopView }: { isDesktopView: boolean }) => {
  const { activeTenant } = useGlobalApp();
  const history = useHistory();
  const searchSelectRef = useRef<HTMLElement>();
  const isFocusedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery.trim(), 300, { leading: true });
  const { searchResults, isLoading } = useGlobalSearch(debouncedSearchQuery);

  const options = debouncedSearchQuery
    ? searchResults.map((category, categoryIdx) => ({
        label: <Category category={category} />,
        options: category.options.map((item, itemIdx) => ({
          value: `${categoryIdx}:${itemIdx}`,
          label: <Option option={item} isDesktopView={isDesktopView} />,
          href: item.href,
          categoryLabel: category.label,
          itemLabel: item.label,
          onSelect: item?.onSelect,
        })),
      }))
    : [];
  const numOptions = options.reduce((sum, category) => sum + category.options.length, 0);

  const isPlatformMac = getIsPlatformMac();
  const searchPromptStr = isDesktopView
    ? `Search inventory, ${
        shouldShowProduct(activeTenant, TenantProductName.DfpaSearchAndLookup) ? 'POs' : 'customers'
      }, orders, navigation, etc. (${isPlatformMac ? '⌘' : 'Ctrl'}+k)`
    : 'Search';

  const onSelect = (_key: string, option: FIXME) => {
    if (option?.onSelect) {
      option.onSelect();
    }
    track(TrackEvent.Nav_GlobalSearch_OptionSelect, {
      searchQuery,
      searchQueryLength: searchQuery.length,
      categoryLabel: option.categoryLabel,
      optionLabel: option.itemLabel,
      optionHref: option.href,
      numOptions,
    });
    history.push(option.href);
    setSearchQuery('');
  };

  // focus search select when user presses cmd+k or ctrl+k, blur when user presses esc
  useEventListener(document, 'keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'k' && (ev.metaKey || ev.ctrlKey)) {
      // Edge on windows steals ctrl+k for main location bar
      ev.stopPropagation();
      ev.preventDefault();
      if (!isFocusedRef.current) {
        track(TrackEvent.Nav_GlobalSearch_Focus, { interactType: 'KeyboardShortcut' });
      }
      searchSelectRef.current?.focus();
    } else if (ev.key === 'Escape') {
      searchSelectRef.current?.blur();
    }
  });

  return (
    <Styled.SearchBox
      ref={searchSelectRef}
      data-track="Nav.SearchSelect"
      showSearch
      value={searchPromptStr}
      size="small"
      onSearch={setSearchQuery}
      onSelect={onSelect}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={() => {
        isFocusedRef.current = false;
        setSearchQuery('');
        // NOTE: blur doesn't fire when user selects an option, only when user clicks outside search input
        track(TrackEvent.Nav_GlobalSearch_Abandon, {
          searchQuery,
          searchQueryLength: searchQuery.length,
          numOptions,
        });
      }}
      onMouseDown={() => {
        // using mousedown because it fires before focus
        if (!isFocusedRef.current) {
          track(TrackEvent.Nav_GlobalSearch_Focus, { interactType: 'Mouse' });
        }
      }}
      options={options}
      // only show no results if user entered search query
      // initial state is search select looks like an input
      notFoundContent={
        debouncedSearchQuery.length ? (
          isLoading ? (
            <LoadingOptionsSkeleton />
          ) : (
            'No search results found, try another search'
          )
        ) : null
      }
      filterOption={false}
      dropdownStyle={{ position: `fixed` }}
    />
  );
};

function LoadingOptionsSkeleton() {
  return (
    <FlexSpace
      direction="column"
      gap={10}
      className={css`
        padding: 32px 16px 0px 16px;
      `}
    >
      <MiniTableRowSkeleton />
      <MiniTableRowSkeleton />
      <MiniTableRowSkeleton />
    </FlexSpace>
  );
}
